import React, { useState } from 'react';
import { privacyUtils } from '../../config/statsig';

interface StatsigPrivacyDemoProps {
  className?: string;
}

export const StatsigPrivacyDemo: React.FC<StatsigPrivacyDemoProps> = ({ className = '' }) => {
  const [sensitiveData, setSensitiveData] = useState({
    email: '',
    phone: '',
    ssn: '',
    creditCard: '',
    password: '',
    personalNotes: ''
  });

  const [showSensitiveInfo, setShowSensitiveInfo] = useState(false);

  const handleInputChange = (field: keyof typeof sensitiveData, value: string) => {
    setSensitiveData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className={`privacy-controls-demo p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg ${className}`}>
      <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
        Statsig Privacy Demo
      </h2>
      
      <div className="space-y-6">
        {/* Blocked Content Section */}
        <section>
          <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">
            🚫 Blocked Content (Not Recorded)
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Content in this section is completely blocked from session replay using <code>sr-block</code> class and <code>data-sensitive</code> attribute.
          </p>
          
          {/* Blocked using CSS class */}
          <div className="sr-block p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
            <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">Sensitive Personal Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={sensitiveData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={sensitiveData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          </div>
          
          {/* Blocked using data attribute */}
          <div data-sensitive className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
            <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">Financial Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                  SSN
                </label>
                <input
                  type="text"
                  value={sensitiveData.ssn}
                  onChange={(e) => handleInputChange('ssn', e.target.value)}
                  placeholder="XXX-XX-XXXX"
                  className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                  Credit Card
                </label>
                <input
                  type="text"
                  value={sensitiveData.creditCard}
                  onChange={(e) => handleInputChange('creditCard', e.target.value)}
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Masked Content Section */}
        <section>
          <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">
            🎭 Masked Content (Recorded but Hidden)
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Input values are masked in session replay but interactions are still recorded.
          </p>
          
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
                  Password (Always Masked)
                </label>
                <input
                  type="password"
                  value={sensitiveData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Enter password"
                  className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
                  Text Input (Masked)
                </label>
                <input
                  type="text"
                  value={sensitiveData.personalNotes}
                  onChange={(e) => handleInputChange('personalNotes', e.target.value)}
                  placeholder="Personal notes..."
                  className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
                Textarea (Masked)
              </label>
              <textarea
                value={sensitiveData.personalNotes}
                onChange={(e) => handleInputChange('personalNotes', e.target.value)}
                placeholder="Enter personal notes or sensitive information..."
                rows={3}
                className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
          </div>
        </section>

        {/* Ignored Content Section */}
        <section>
          <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">
            👻 Ignored Content (Not Recorded)
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Non-critical interactions that don't need to be recorded for analysis.
          </p>
          
          <div className="p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded">
            <div className="flex flex-wrap gap-2">
              <button className="sr-ignore px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">
                Non-critical Button
              </button>
              
              <button className="sr-ignore px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">
                Analytics Opt-out
              </button>
              
              <button className="sr-ignore px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">
                Cookie Preferences
              </button>
            </div>
          </div>
        </section>

        {/* Safe Content Section */}
        <section>
          <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">
            ✅ Safe Content (Fully Recorded)
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Non-sensitive content that's safe to record for user experience analysis.
          </p>
          
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                  Date Picker (Safe)
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                  Color Picker (Safe)
                </label>
                <input
                  type="color"
                  className="w-full h-10 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                  Range Slider (Safe)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                  Select Dropdown (Safe)
                </label>
                <select className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option>Option 1</option>
                  <option>Option 2</option>
                  <option>Option 3</option>
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy Information Section */}
        <section>
          <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">
            ℹ️ Privacy Information
          </h3>
          
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
            <div className="space-y-3 text-sm text-blue-800 dark:text-blue-200">
              <div>
                <strong>Session Replay Sampling:</strong> {import.meta.env.MODE === 'production' ? '10%' : '100%'} of sessions are recorded
              </div>
              <div>
                <strong>Error Session Sampling:</strong> {import.meta.env.MODE === 'production' ? '50%' : '100%'} of error sessions are recorded
              </div>
              <div>
                <strong>Data Retention:</strong> Session recordings are automatically deleted after 30 days
              </div>
              <div>
                <strong>Privacy Controls:</strong> Sensitive data is automatically masked or blocked from recordings
              </div>
              <div>
                <strong>Cross-Origin Content:</strong> Content from other domains is not recorded
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
              <button
                onClick={() => setShowSensitiveInfo(!showSensitiveInfo)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                {showSensitiveInfo ? 'Hide' : 'Show'} Privacy Implementation Details
              </button>
              
              {showSensitiveInfo && (
                <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-800/30 rounded text-xs font-mono">
                  <div><strong>CSS Classes:</strong></div>
                  <div>• .sr-block - Completely blocks element from recording</div>
                  <div>• .sr-ignore - Ignores interactions with element</div>
                  <div>• .sr-mask-text - Masks text content</div>
                  
                  <div className="mt-2"><strong>Data Attributes:</strong></div>
                  <div>• data-sensitive - Marks element as sensitive (blocked)</div>
                  <div>• data-private - Marks element as private (blocked)</div>
                  <div>• data-mask-text - Masks text content</div>
                  
                  <div className="mt-2"><strong>Input Masking:</strong></div>
                  <div>• Email, phone, text, textarea, password inputs are masked</div>
                  <div>• Date, color, range, select inputs are not masked</div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StatsigPrivacyDemo; 